import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { PageHeader } from "../../components/layout/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import {
  TrendingUp,
  Star,
  BookOpen,
  CheckCircle,
  Flame,
  Crown,
  Heart,
  ClipboardList,
} from "lucide-react";
import grade3Data from "../../data/grade3/grade3_assignments.v2.json";
import type { Grade3Activity, Grade3Lesson, Grade3Workbook } from "../../types/grade3Workbook";
import {
  readActivityScores,
  readWorkbookJson,
  TKM_G3_TOC_COMPLETION_KEY,
  type WorkbookActivityCompletion,
} from "../../components/assignments/workbookPersist";
import { ROUTES } from "../../utils/constants";

const grade3Workbook = grade3Data as Grade3Workbook;

function grade3PersistenceId(lesson: Grade3Lesson, act: Grade3Activity): string {
  const sub = act.sub_activity != null ? String(act.sub_activity) : "x";
  return `G${grade3Workbook.grade}-L${lesson.lesson_no}-A${act.activity_no}-${sub}-${act.ui_type}`;
}

export const ProgressPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"overall" | "lessons" | "achievements">("overall");
  const [g3WorkbookTick, setG3WorkbookTick] = useState(0);

  useEffect(() => {
    const bump = () => setG3WorkbookTick((n) => n + 1);
    window.addEventListener("tkm-g3-scores-updated", bump);
    return () => window.removeEventListener("tkm-g3-scores-updated", bump);
  }, []);

  const grade3LessonRows = useMemo(() => {
    void g3WorkbookTick;
    const scores = readActivityScores();
    const completion = readWorkbookJson<Record<string, WorkbookActivityCompletion>>(
      TKM_G3_TOC_COMPLETION_KEY,
      {}
    );
    return grade3Workbook.lessons.map((lesson) => ({
      lesson,
      activities: lesson.activities.map((act) => {
        const persistenceId = grade3PersistenceId(lesson, act);
        const status = completion[persistenceId] ?? "not_started";
        const scorePct = scores[persistenceId]?.pct;
        return { act, persistenceId, status, scorePct };
      }),
    }));
  }, [g3WorkbookTick]);

  const progressData = {
    overall: {
      title: "Overall Progress",
      completion: 87,
      level: 7,
      totalStars: 1564,
      streak: 12,
      lessonsCompleted: 47,
    },
    lessons: {
      title: "Lessons Progress",
      data: [
        { name: "தமிழ் எழுத்துகள்", progress: 100, stars: 5, completed: true },
        { name: "வாக்கியங்கள்", progress: 92, stars: 4.8, completed: true },
        { name: "கதைகள்", progress: 78, stars: 4.5, completed: false },
        { name: "இலக்கணம்", progress: 65, stars: 4.2, completed: false },
        { name: "பேச்சு", progress: 45, stars: 3.9, completed: false },
      ],
    },
    achievements: {
      title: "Achievements",
      badges: [
        { name: "Beginner", icon: "⭐", unlocked: true, date: "Jan 15" },
        { name: "Word Master", icon: "⭐⭐", unlocked: true, date: "Feb 2" },
        { name: "Story Teller", icon: "⭐⭐⭐", unlocked: true, date: "Feb 20" },
        { name: "Super Writer", icon: "⭐⭐⭐⭐", unlocked: false },
        { name: "Tamil Hero", icon: "⭐⭐⭐⭐⭐", unlocked: false },
      ],
    },
  };

  const renderProgressBar = (percentage: number, color: string) => (
    <div className="w-full bg-black/30 rounded-full h-4 backdrop-blur-sm overflow-hidden border border-white/20">
      <div
        className={`h-4 rounded-full shadow-lg transition-all duration-700 ${color}`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );

  return (
    <Layout>
      <PageHeader
        title="🚀 Your Progress Journey! 🌟"
        subtitle="See how far you've come in Tamil learning! 📈"
      />

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-12">
        {/* Tab Navigation */}
<div className="flex flex-wrap justify-center gap-4 mb-12">
  {Object.keys(progressData).map((tabKey) => {
    const tab = tabKey as "overall" | "lessons" | "achievements";
    const active = activeTab === tab;

    return (
      <Button
        key={tab}
        variant={active ? "primary" : "outline"} // ✅ FIXED (no "default")
        className={`font-black text-xl py-6 px-8 rounded-3xl shadow-xl ${
          active
            ? "text-white bg-gradient-to-r from-purple-500 to-pink-500 shadow-purple-500/50"
            : "text-white bg-black/20 backdrop-blur-sm border border-white/30 hover:bg-white/10"
        }`}
        onClick={() => setActiveTab(tab)}
      >
        {tab === "overall" && "📊 Overall"}
        {tab === "lessons" && "📚 Lessons"}
        {tab === "achievements" && "🏆 Achievements"}
      </Button>
    );
  })}
</div>


        {/* Overall Progress */}
        {activeTab === "overall" && (
          <Card className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white shadow-2xl border-0 overflow-hidden">
            <CardHeader className="text-center pb-4">
              <div className="inline-flex items-center gap-3 bg-black/20 backdrop-blur-sm px-8 py-4 rounded-3xl mb-8 border border-white/20">
                <Crown className="w-12 h-12 text-yellow-300 drop-shadow-lg" />
                <div>
                  <h2 className="text-4xl font-black drop-shadow-2xl">
                    Level {progressData.overall.level}
                  </h2>
                  <p className="text-2xl font-bold text-yellow-200">
                    Tamil Superstar 🌟
                  </p>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="group">
                  <div className="text-center mb-6 p-6 bg-black/20 backdrop-blur-sm rounded-3xl group-hover:bg-black/30 transition-all border border-white/20">
                    {renderProgressBar(
                      progressData.overall.completion,
                      "bg-gradient-to-r from-emerald-400 to-green-500"
                    )}
                    <p className="text-5xl font-black text-yellow-300 mt-4 drop-shadow-lg">
                      {progressData.overall.completion}%
                    </p>
                    <p className="text-lg font-bold text-white/95 mt-2">
                      Overall Mastery
                    </p>
                  </div>
                </div>

                <div className="text-center p-8 bg-black/20 backdrop-blur-sm rounded-3xl group hover:bg-black/30 hover:scale-105 transition-all border border-white/20">
                  <Star className="w-16 h-16 text-yellow-300 mx-auto mb-4 group-hover:animate-pulse drop-shadow-2xl" />
                  <p className="text-4xl font-black text-yellow-200 mb-2">
                    {progressData.overall.totalStars.toLocaleString()}
                  </p>
                  <p className="text-xl font-black text-white/95">
                    Total Stars ⭐
                  </p>
                </div>

                <div className="text-center p-8 bg-black/20 backdrop-blur-sm rounded-3xl group hover:bg-black/30 hover:scale-105 transition-all border border-white/20">
                  <Flame className="w-16 h-16 text-orange-300 mx-auto mb-4 group-hover:animate-pulse drop-shadow-2xl" />
                  <p className="text-4xl font-black text-orange-200 mb-2">
                    {progressData.overall.streak}
                  </p>
                  <p className="text-xl font-black text-white/95">
                    Day Streak 🔥
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6 pt-8 border-t border-white/30">
                <div className="text-center">
                  <p className="text-3xl font-black text-yellow-200 mb-4">
                    {progressData.overall.lessonsCompleted}
                  </p>
                  <p className="text-xl font-black text-white/95">
                    Lessons Completed 📚
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-black text-yellow-200 mb-4">
                    +342
                  </p>
                  <p className="text-xl font-black text-white/95">
                    This Week ⭐
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lessons Progress */}
        {activeTab === "lessons" && (
          <Card className="bg-gradient-to-br from-emerald-500 via-teal-500 to-green-600 text-white shadow-2xl border-0">
            <CardHeader>
              <CardTitle className="text-3xl flex items-center gap-3 font-black">
                <BookOpen className="w-12 h-12" />
                Lessons Progress 📚
              </CardTitle>
            </CardHeader>

            <CardContent>
              <div className="mb-10 rounded-2xl border border-white/30 bg-black/15 p-5 backdrop-blur-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-white">
                    <ClipboardList className="h-8 w-8 shrink-0 text-emerald-200" aria-hidden />
                    <div>
                      <p className="text-lg font-black tamil-text" lang="ta">
                        {grade3Workbook.book} — பயிற்சிப் புத்தகம் (பாடம் {grade3Workbook.grade})
                      </p>
                      <p className="text-sm font-semibold text-emerald-100/90">
                        Each lesson lists activities with status and your last score on this device.
                      </p>
                    </div>
                  </div>
                  <Link
                    to={ROUTES.STUDENT_ACTIVITY}
                    className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-white/95 px-4 py-2 text-sm font-black text-emerald-800 shadow-md transition hover:bg-white"
                  >
                    Open workbook
                  </Link>
                </div>
                <div className="mt-5 max-h-[min(28rem,55vh)] space-y-4 overflow-y-auto pr-1">
                  {grade3LessonRows.map(({ lesson, activities }) => (
                    <div
                      key={lesson.lesson_no}
                      className="rounded-xl border border-white/20 bg-black/20 p-4 text-left text-white"
                    >
                      <p className="text-base font-black tamil-text text-emerald-100" lang="ta">
                        பாடம் {lesson.lesson_no}: {lesson.title}
                      </p>
                      <ul className="mt-3 space-y-2">
                        {activities.map(({ act, persistenceId, status, scorePct }) => (
                          <li
                            key={persistenceId}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-black/25 px-3 py-2 text-sm"
                          >
                            <span className="min-w-0 flex-1 font-bold tamil-text text-white/95" lang="ta">
                              {act.activity_no}. {act.title}
                            </span>
                            <span className="flex shrink-0 flex-wrap items-center gap-2">
                              {scorePct != null ? (
                                <Badge className="border-amber-300/50 bg-amber-500/25 font-black text-amber-100">
                                  {scorePct}%
                                </Badge>
                              ) : (
                                <Badge className="border-white/20 bg-white/10 font-semibold text-white/80">
                                  —
                                </Badge>
                              )}
                              <Badge
                                className={
                                  status === "completed"
                                    ? "border-emerald-300/60 bg-emerald-500/30 font-bold text-emerald-50"
                                    : status === "in_progress"
                                      ? "border-sky-300/50 bg-sky-500/25 font-bold text-sky-50"
                                      : "border-white/20 bg-white/10 font-bold text-white/70"
                                }
                              >
                                {status === "completed"
                                  ? "Done"
                                  : status === "in_progress"
                                    ? "In progress"
                                    : "Not started"}
                              </Badge>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                {progressData.lessons.data.map((lesson, index) => (
                  <Card
                    key={index}
                    className="group cursor-pointer hover:shadow-2xl hover:scale-[1.02] transition-all bg-black/20 backdrop-blur-sm border border-white/20 hover:border-yellow-300/50"
                  >
                    <CardContent className="p-8 pt-0">
                      <div className="flex items-start gap-6">
                        <div
                          className={`flex-shrink-0 w-3 h-20 rounded-full opacity-75 group-hover:opacity-100 transition-all bg-gradient-to-t ${
                            lesson.completed
                              ? "from-emerald-400 to-green-500"
                              : "from-orange-400 to-red-500"
                          }`}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-4">
                            <h3 className="text-2xl font-black text-white drop-shadow-lg">
                              {lesson.name}
                            </h3>
                            {lesson.completed ? (
                              <Badge className="bg-emerald-400/30 text-emerald-100 border-emerald-400 px-4 py-2 shadow-lg font-bold">
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Completed
                              </Badge>
                            ) : (
                              <Badge className="bg-orange-400/30 text-orange-100 border-orange-400 px-4 py-2 shadow-lg font-bold">
                                In Progress
                              </Badge>
                            )}
                          </div>

                          {renderProgressBar(
                            lesson.progress,
                            lesson.completed
                              ? "bg-gradient-to-r from-emerald-400 to-green-500"
                              : "bg-gradient-to-r from-yellow-400 to-orange-500"
                          )}

                          <div className="flex items-center justify-between mt-6">
                            <div className="flex items-center gap-2">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-6 h-6 ${
                                    i < Math.floor(lesson.stars)
                                      ? "text-yellow-400 fill-yellow-400 drop-shadow-lg"
                                      : "text-yellow-200"
                                  }`}
                                />
                              ))}
                              <span className="font-black text-xl text-yellow-200">
                                ({lesson.stars})
                              </span>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-black text-yellow-300">
                                {lesson.progress}%
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Achievements (✅ FIXED: was wrongly under lessons) */}
        {activeTab === "achievements" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {progressData.achievements.badges.map((badge, index) => (
              <Card
                key={index}
                className={`group hover:scale-105 transition-all shadow-xl border-0 overflow-hidden ${
                  badge.unlocked
                    ? "bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-emerald-500/25"
                    : "bg-gradient-to-br from-slate-800 to-slate-900 text-white shadow-slate-900/50"
                }`}
              >
                <CardContent className="p-8 text-center pt-12 relative">
                  <div
                    className={`absolute -top-8 left-1/2 transform -translate-x-1/2 w-24 h-24 bg-black/20 backdrop-blur-sm rounded-3xl flex items-center justify-center shadow-2xl group-hover:scale-110 transition-all ${
                      badge.unlocked ? "ring-4 ring-yellow-400/50" : "ring-2 ring-white/10"
                    }`}
                  >
                    <div
                      className={`text-3xl font-black drop-shadow-2xl ${
                        badge.unlocked ? "text-yellow-300" : "text-white/40"
                      }`}
                    >
                      {badge.icon}
                    </div>
                  </div>

                  <h3 className="text-2xl font-black mb-4 drop-shadow-lg">
                    {badge.name}
                  </h3>

                  {badge.unlocked ? (
                    <div className="flex items-center justify-center gap-2 mb-6 text-emerald-200 font-bold">
                      <CheckCircle className="w-6 h-6 animate-pulse" />
                      <span className="text-lg">Unlocked!</span>
                      {badge.date && (
                        <span className="text-sm bg-black/20 px-3 py-1 rounded-full border border-white/20">
                          {badge.date}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-white/70 mb-6">
                      <p className="text-lg font-black italic">Coming Soon!</p>
                      <div className="flex items-center justify-center gap-2 mt-2 text-sm opacity-90 font-bold">
                        <Heart className="w-4 h-4" />
                        Keep Learning
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Weekly Summary */}
        <Card className="bg-gradient-to-r from-orange-500 via-red-500 to-yellow-500 text-black shadow-2xl border-0 text-center overflow-hidden">
          <CardContent className="py-16 px-8">
            <TrendingUp className="w-24 h-24 mx-auto mb-8 text-white/80 drop-shadow-2xl animate-bounce" />
            <h2 className="text-5xl font-black mb-6 drop-shadow-2xl">
              This Week's Growth! 🚀
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto mb-12">
              <div className="bg-white/70 backdrop-blur-sm p-8 rounded-3xl shadow-2xl">
                <p className="text-4xl font-black text-orange-700 mb-2">+89</p>
                <p className="text-xl font-black">Stars Earned ⭐</p>
              </div>
              <div className="bg-white/70 backdrop-blur-sm p-8 rounded-3xl shadow-2xl">
                <p className="text-4xl font-black text-orange-700 mb-2">5</p>
                <p className="text-xl font-black">New Lessons 📚</p>
              </div>
              <div className="bg-white/70 backdrop-blur-sm p-8 rounded-3xl shadow-2xl">
                <p className="text-4xl font-black text-orange-700 mb-2">+12%</p>
                <p className="text-xl font-black">Progress Boost 📈</p>
              </div>
            </div>

            <Button className="bg-white/95 text-orange-700 font-black text-2xl py-8 px-20 rounded-4xl shadow-2xl hover:scale-105 border-4 border-white/70">
              🎉 Keep Going Superstar!
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ProgressPage;
